import { createFileRoute, Link } from "@tanstack/react-router";
import impact from "@/assets/impact.jpg";
import { MarketingHeroImage } from "@/components/site/MarketingHeroImage";
import {
  getPlayInjuryFreeAudienceCallouts,
  getPlayInjuryFreeHero,
  getPlayInjuryFreeSections,
} from "@/lib/play-injury-free";
import { HERO_IMAGE_SIZES, heroPreloadLink } from "@/lib/marketing-images";
import { impactPic } from "@/lib/marketing-images.impact";
import { pageHead } from "@/lib/seo";
import { m } from "@/paraglide/messages.js";
import { ArrowRight } from "lucide-react";

export const Route = createFileRoute("/play-injury-free")({
  head: () => {
    const head = pageHead({
      title: m.injury_meta_title(),
      description: m.injury_meta_description(),
      path: "/play-injury-free",
      image: impact,
    });
    return {
      ...head,
      links: [...(head.links ?? []), heroPreloadLink(impactPic)],
    };
  },
  component: PlayInjuryFreePage,
});

function PlayInjuryFreePage() {
  const hero = getPlayInjuryFreeHero();
  const sections = getPlayInjuryFreeSections();
  const callouts = getPlayInjuryFreeAudienceCallouts();

  return (
    <>
      <section className="relative isolate overflow-hidden bg-ink">
        <MarketingHeroImage
          picture={impactPic}
          alt=""
          sizes={HERO_IMAGE_SIZES}
          priority
          className="absolute inset-0 h-full w-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/85 to-ink/30" />
        <div className="container-page relative page-hero-pad">
          <span className="eyebrow">{m.footer_link_method()}</span>
          <h1 className="mt-4 max-w-3xl font-display text-hero leading-[0.95] md:text-8xl">
            {m.injury_title()}
          </h1>
          <p className="mt-6 max-w-2xl text-foreground/80 md:text-lg">{hero.subtitle}</p>
        </div>
      </section>

      <section className="container-page page-follow-pad">
        <div className="mx-auto mt-14 max-w-3xl space-y-10">
          {sections.map((section) => (
            <section key={section.heading}>
              <h2 className="font-display text-2xl md:text-3xl">{section.heading}</h2>
              <div className="mt-4 space-y-3">
                {section.paragraphs.map((paragraph) => (
                  <p
                    key={paragraph}
                    className="text-sm leading-relaxed text-foreground/75 md:text-base"
                  >
                    {paragraph}
                  </p>
                ))}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-16">
          <h2 className="text-center font-display text-3xl md:text-4xl">{m.injury_built_for()}</h2>
          <p className="mx-auto mt-3 max-w-2xl text-center text-sm text-foreground/70">
            {m.injury_built_for_body()}
          </p>
          <div className="mt-10 grid gap-px bg-white/10 md:grid-cols-3">
            {callouts.map((callout) => (
              <div key={callout.label} className="bg-ink p-8">
                <h3 className="font-display text-lg text-ember">{callout.label}</h3>
                <p className="mt-3 text-sm leading-relaxed text-foreground/75">{callout.body}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-16 flex flex-col items-center gap-4 text-center">
          <Link
            to="/method"
            className="inline-flex items-center gap-2 font-display text-sm tracking-[0.18em] text-foreground/90 hover:text-ember"
          >
            {m.injury_explore_steps()} <ArrowRight className="h-4 w-4 rtl:rotate-180" />
          </Link>
        </div>

        <div className="mt-12 flex flex-col items-center text-center">
          <h3 className="font-display text-4xl md:text-5xl">{m.method_ready()}</h3>
          <p className="mt-3 max-w-lg text-foreground/70">{m.method_ready_hint()}</p>
          <Link to="/schedule" className="btn-ember btn-ember-hover mt-8">
            {m.nav_book_lesson()} <ArrowRight className="h-5 w-5 rtl:rotate-180" />
          </Link>
        </div>
      </section>
    </>
  );
}
